import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import CustomTable from '../../../shared/CustomTable'
import CustomButton from '../../../shared/CustomButton'
import { EyeIcon } from '../../../utils/TableActionIcons'
import { formatDateTime } from '../../../utils/FormateDateTime'
import CustomChip from '../../../shared/CustomChip'
import ReusableModal from '../../../shared/ReuseableModal'
import BulkProcessingLogs from './BulkProcessingLogs'
import { AiOutlineHistory } from 'react-icons/ai'
import CustomPagination from '../../../shared/CustomPagination'
import { useGetBulkClaimsBatchesQuery } from '../../../redux/api/modules/batchesApi'

const STORAGE_KEY = 'bulkClaimsBatchesPagination'

function BulkClaimsBatchesTab() {
  const { t } = useTranslation()
  const [showLogsModal, setShowLogsModal] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState(null)
  const [pagination, setPagination] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
      return {
        page: Number(stored?.page) || 1,
        per_page: Number(stored?.per_page) || 10
      }
    } catch (error) {
      return {
        page: 1,
        per_page: 10
      }
    }
  })
  const { page, per_page: perPage } = pagination

  const {
    data: response,
    isLoading: queryLoading,
    isFetching: queryFetching,
    refetch
  } = useGetBulkClaimsBatchesQuery({ page, per_page: perPage })

  const loading = queryLoading || queryFetching
  const data = response?.jobs || []
  const totalCount = response?.total_count || 0
  const totalPages = response?.total_pages || (totalCount && perPage ? Math.ceil(totalCount / perPage) : 1)

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ page: pagination.page, per_page: pagination.per_page })
    )
  }, [pagination.page, pagination.per_page])

  const handleViewLogs = jobId => {
    setSelectedJobId(jobId)
    setShowLogsModal(true)
  }

  const onPageChange = (newPage, newRowsPerPage) => {
    setPagination({ page: newPage, per_page: newRowsPerPage })
  }

  const getStatusChipType = status => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success'
      case 'processing':
        return 'warning'
      case 'queued':
      case 'pending':
        return 'default'
      case 'stopped':
        return 'warning'
      case 'failed':
        return 'danger'
      default:
        return 'default'
    }
  }

  const columns = [
    {
      header: t('Actions'),
      body: row => (
        <div className='flex gap-2 items-center'>
          <EyeIcon
            onClick={() => handleViewLogs(row?.job_id)}
            title={t('View Details')}
          />
        </div>
      )
    },
    {
      header: t('Type'),
      body: row => (
        row?.job_type === 'process' ? t('Process') : t('Submit')
      )
    },
    {
      header: t('Progress'),
      body: row => (
        <div className='flex items-center gap-2'>
          <span className='text-xs'>{row?.progress_percentage?.toFixed(0) || 0}%</span>
        </div>
      )
    },
    {
      header: t('Total Visits'),
      body: row => row?.total_visits || 0
    },
    {
      header: t('Success'),
      body: row => (
        <span className='text-green-600 font-semibold'>{row?.success_count || 0}</span>
      )
    },
    {
      header: t('Failed'),
      body: row => (
        <span className='text-red-600 font-semibold'>{row?.failed_count || 0}</span>
      )
    },
    {
      header: t('Created At'),
      body: row => formatDateTime(row?.created_at) || '-'
    },
    {
      header: t('Completed At'),
      body: row => row?.completed_at ? formatDateTime(row?.completed_at) : '-'
    },
    {
      header: t('Status'),
      body: row => (
        <CustomChip
          label={row?.status?.charAt(0).toUpperCase() + row?.status?.slice(1) || 'Unknown'}
          chipType={getStatusChipType(row?.status)}
          className='h-[22px] rounded-full px-2 py-1'
        />
      )
    }
  ]

  return (
    <div>
      <div className='flex items-center justify-end mb-2'>
        <CustomButton
          title={t('Refresh')}
          icon='pi pi-refresh'
          buttonType='primary'
          className='w-fit h-[35px]'
          onClick={() => refetch()}
          loading={loading}
        />
      </div>
      <CustomTable
        data={data}
        isLoading={loading}
        columns={columns}
        emptyMessage={t('No bulk claim batches found')}
      />

      {data?.length > 0 && (
        <CustomPagination
          currentPage={page}
          rowsPerPage={perPage}
          totalRecords={totalCount}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}

      <ReusableModal
        position='top'
        isVisible={showLogsModal}
        title={t('Bulk Batch Details')}
        HeaderIcon={AiOutlineHistory}
        onClose={() => {
          setShowLogsModal(false)
          setSelectedJobId(null)
        }}
        className='w-full lg:w-[1000px]'
      >
        <BulkProcessingLogs
          jobId={selectedJobId}
          onComplete={() => refetch()}
          onStatusChange={() => refetch()}
          onClose={() => {
            setShowLogsModal(false)
            setSelectedJobId(null)
          }}
        />
      </ReusableModal>
    </div>
  )
}

export default BulkClaimsBatchesTab
